import { t } from 'i18next';

import { ExternalProjectAssociationStatus, ExternalProjectAssociationType } from 'projects/enums';
import {
  IExternalProject,
  IExternalProjectAccount,
  IProgramProject,
  IProgramProjectAccount,
} from 'projects/types';
import { isExternalProgram, isExternalProjectAccountActive } from 'projects/utils';

export interface IGroupedAccount extends Omit<IExternalProjectAccount, 'associationType'> {
  id: string;
  associationType: string[];
}

type TExternalCustomerRowKeys = 'accountNumber' | 'associationType' | 'id' | 'isLinkActive';

// Accounts reached only through a program's pair projects carry no address data; their association
// types are the ones they have across the pair projects (bill-to and/or ship-to), and they stay
// active while at least one of those links is active.
export interface IExternalCustomerRow
  extends Pick<IGroupedAccount, TExternalCustomerRowKeys>,
    Partial<Omit<IGroupedAccount, TExternalCustomerRowKeys>> {
  isViaProject: boolean;
  pairedProjects: IProgramProject[];
}

export const groupAccountsByNumber = (accounts: IExternalProjectAccount[]): IGroupedAccount[] => {
  const groupedMap = accounts.reduce<Record<string, IGroupedAccount>>((acc, account) => {
    const { accountNumber, associationType, ...accountData } = account;

    const existingGroup = acc[accountNumber];

    const currentAssociationTypes = existingGroup?.associationType ?? [];

    const updatedAssociationTypes =
      associationType && !currentAssociationTypes.includes(associationType)
        ? [...currentAssociationTypes, associationType]
        : currentAssociationTypes;

    // One row per account: it stays Active while at least one of its links is active.
    const isGroupActive =
      (!!existingGroup && isExternalProjectAccountActive(existingGroup)) ||
      isExternalProjectAccountActive(account);

    acc[accountNumber] = {
      id: accountNumber,
      accountNumber,
      associationType: updatedAssociationTypes,
      ...accountData,
      isLinkActive: isGroupActive,
    };

    return acc;
  }, {});

  return Object.values(groupedMap);
};

export const getAssociationStatusLabelMap = (): Record<
  ExternalProjectAssociationStatus,
  string
> => ({
  [ExternalProjectAssociationStatus.ACTIVE]: t('labelActive'),
  [ExternalProjectAssociationStatus.INACTIVE]: t('labelInactive'),
});

export const getAssociationStatus = (isLinkActive?: boolean): ExternalProjectAssociationStatus =>
  isLinkActive
    ? ExternalProjectAssociationStatus.ACTIVE
    : ExternalProjectAssociationStatus.INACTIVE;

export const getAssociationStatusLabel = (status?: ExternalProjectAssociationStatus): string =>
  getAssociationStatusLabelMap()[status] ?? '';

export const MAX_DISPLAYED_PAIRED_PROJECTS = 1;

export type TPairedProjectField = keyof Pick<IProgramProject, 'projectName' | 'projectNumber'>;

export const getPairedProjectValues = (
  data: IExternalCustomerRow | undefined,
  field: TPairedProjectField
): string[] =>
  (data?.pairedProjects ?? []).map((pairedProject) => pairedProject[field]).filter(Boolean);

export const joinValues = (values?: string[]): string => values?.join(' ') ?? '';

// Fixed order so a via-project row lists its chips the same way whichever project brings each type.
const PAIR_PROJECT_ACCOUNT_LISTS: [
  ExternalProjectAssociationType,
  (pairProject: IProgramProject) => IProgramProjectAccount[] | undefined,
][] = [
  [ExternalProjectAssociationType.BILL_TO_ACCOUNT, (pairProject) => pairProject.billToAccounts],
  [ExternalProjectAssociationType.SHIP_TO_ACCOUNT, (pairProject) => pairProject.shipToAccounts],
];

const referencesAccount = (pairProject: IProgramProject, accountNumber: string) =>
  PAIR_PROJECT_ACCOUNT_LISTS.some(([, getAccounts]) =>
    (getAccounts(pairProject) ?? []).some((account) => account?.accountNumber === accountNumber)
  );

const getPairOnlyRows = (
  pairProjects: IProgramProject[],
  ownAccountNumbers: Set<string>
): IExternalCustomerRow[] => {
  const pairOnlyRows = new Map<string, IExternalCustomerRow>();

  PAIR_PROJECT_ACCOUNT_LISTS.forEach(([associationType, getAccounts]) => {
    pairProjects.forEach((pairProject) => {
      (getAccounts(pairProject) ?? []).forEach((account) => {
        const { accountNumber, customerName } = account;

        if (!accountNumber || ownAccountNumbers.has(accountNumber)) {
          return;
        }

        const existingRow = pairOnlyRows.get(accountNumber);
        const isLinkActive = isExternalProjectAccountActive(account);

        if (!existingRow) {
          pairOnlyRows.set(accountNumber, {
            id: accountNumber,
            accountNumber,
            customerName,
            associationType: [associationType],
            isLinkActive,
            isViaProject: true,
            pairedProjects: [pairProject],
          });

          return;
        }

        if (!existingRow.associationType.includes(associationType)) {
          existingRow.associationType.push(associationType);
        }

        existingRow.isLinkActive = existingRow.isLinkActive || isLinkActive;

        if (!existingRow.customerName && customerName) {
          existingRow.customerName = customerName;
        }

        if (!existingRow.pairedProjects.includes(pairProject)) {
          existingRow.pairedProjects.push(pairProject);
        }
      });
    });
  });

  // Keep the paired projects in program order even when a later list brings an earlier project.
  return Array.from(pairOnlyRows.values()).map((row) => ({
    ...row,
    pairedProjects: pairProjects.filter((pairProject) => row.pairedProjects.includes(pairProject)),
  }));
};

export const getExternalCustomerRows = (
  externalProject?: IExternalProject
): IExternalCustomerRow[] => {
  const ownAccountRows = groupAccountsByNumber(externalProject?.accounts ?? []);
  const pairProjects =
    externalProject && isExternalProgram(externalProject) ? externalProject.projects ?? [] : [];

  const rows: IExternalCustomerRow[] = ownAccountRows.map((account) => ({
    ...account,
    isViaProject: false,
    pairedProjects: pairProjects.filter((pairProject) =>
      referencesAccount(pairProject, account.accountNumber)
    ),
  }));

  const ownAccountNumbers = new Set(ownAccountRows.map((account) => account.accountNumber));

  return [...rows, ...getPairOnlyRows(pairProjects, ownAccountNumbers)];
};
